import { fetchClient } from '../lib/openapi-query';
import {
  type CalendarDay,
  type CalendarProgressResponse,
  type DailySchedule,
  type ScheduleResponse,
  type ScheduledTask,
  type TaskCategory,
  type TaskPriority,
  type TaskSection,
  type TaskStats,
  type TaskStatus
} from '../types/tasks';

class TasksService {

  /**
   * Get user's daily schedule for a specific date
   */
  async getDailySchedule(date: string = new Date().toISOString().split('T')[0]): Promise<DailySchedule> {
    try {
      const response = await fetchClient.GET('/schedule', {
        params: {
          query: { date }
        }
      }) as any;
      
      if (response.error) {
        console.error('Failed to fetch daily schedule:', response.error);
        throw new Error(response.error.message ?? 'Unable to fetch daily schedule. Please try again later.');
      }
      
      const scheduleResponse = response.data as unknown as ScheduleResponse;
      
      // Transform schedule response to DailySchedule format
      const schedule: DailySchedule = {
        date: date === 'today' ? new Date().toISOString().split('T')[0] : date,
        sections: scheduleResponse?.sections || [],
        overallProgress: this.calculateOverallProgress(scheduleResponse?.sections || []),
        totalTasks: this.calculateTotalTasks(scheduleResponse?.sections || []),
        completedTasks: this.calculateCompletedTasks(scheduleResponse?.sections || [])
      };

      return schedule;
    } catch (error) {
      console.error('Failed to fetch daily schedule:', error);
      throw new Error('Unable to fetch daily schedule. Please try again later.');
    }
  }

  /**
   * Get weekly calendar progress
   */
  async getCalendarProgress(startDate?: string, endDate?: string): Promise<CalendarDay[]> {
    try {
      const query: any = {};
      if (startDate) query.startDate = startDate;
      if (endDate) query.endDate = endDate;
      
      const response = await fetchClient.GET('/schedule/calendar', {
        params: {
          query
        }
      }) as any;
      
      if (response.error) {
        console.error('Failed to fetch calendar progress:', response.error);
        throw new Error(response.error.message ?? 'Unable to fetch calendar progress. Please try again later.');
      }
      
      const calendarData = response.data as unknown as CalendarProgressResponse;
      
      // Transform response to include additional data
      return (calendarData || []).map((day: any) => ({
        ...day,
        totalTasks: day.totalTasks ?? 0,
        completedTasks: day.completedTasks ?? Math.round((day.progress / 100) * (day.totalTasks ?? 0))
      }));
    } catch (error) {
      console.error('Failed to fetch calendar progress:', error);
      throw new Error('Unable to fetch calendar progress. Please try again later.');
    }
  }

  /**
   * Get current week's calendar progress (convenience method)
   */
  async getWeeklyProgress(): Promise<CalendarDay[]> {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
    
    return this.getCalendarProgress(
      startOfWeek.toISOString().split('T')[0],
      endOfWeek.toISOString().split('T')[0]
    );
  }

  /**
   * Get task statistics for a date range
   */
  async getTaskStats(startDate?: string, endDate?: string): Promise<TaskStats> {
    try {
      const calendarData = await this.getCalendarProgress(startDate, endDate);
      
      const totalTasks = calendarData.reduce((sum, day) => sum + day.totalTasks, 0);
      const completedTasks = calendarData.reduce((sum, day) => sum + day.completedTasks, 0);
      const pendingTasks = totalTasks - completedTasks;
      
      // Calculate streak (consecutive days with > 80% completion)
      let streak = 0;
      for (let i = calendarData.length - 1; i >= 0; i--) {
        if (calendarData[i].progress >= 80) {
          streak++;
        } else {
          break;
        }
      }

      return {
        totalTasks,
        completedTasks,
        pendingTasks,
        skippedTasks: 0, // Not available in current API
        completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
        averageDuration: 30, // Default estimate, not available in current API
        streak
      };
    } catch (error) {
      console.error('Failed to calculate task stats:', error);
      return {
        totalTasks: 0,
        completedTasks: 0,
        pendingTasks: 0,
        skippedTasks: 0,
        completionRate: 0,
        averageDuration: 0,
        streak: 0
      };
    }
  }

  /**
   * Get tasks for a specific date (mock implementation - would need real endpoint)
   */
  async getTasksForDate(date: string): Promise<ScheduledTask[]> {
    try {
      const schedule = await this.getDailySchedule(date);
      
      // Extract tasks from all sections
      const tasks: ScheduledTask[] = [];
      schedule.sections.forEach(section => {
        if (section.tasks) {
          tasks.push(...section.tasks);
        }
      });

      return tasks;
    } catch (error) {
      console.error('Failed to fetch tasks for date:', error);
      throw new Error('Unable to fetch tasks. Please try again later.');
    }
  }

  /**
   * Get formatted time display for task
   */
  getFormattedTime(hour: number, minute: number): string {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = minute.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${period}`;
  }

  /**
   * Get category display name
   */
  getCategoryDisplayName(category: TaskCategory): string {
    const categoryMap: Record<TaskCategory, string> = {
      'hydration': 'Hydration',
      'nutrition': 'Nutrition',
      'movement': 'Movement & Exercise',
      'supplements': 'Supplements',
      'sleep': 'Sleep',
      'mood': 'Mood',
      'stress': 'Stress Management',
      'energy': 'Energy',
      'health': 'Health',
      'other': 'Other'
    };
    return categoryMap[category] || category;
  }

  /**
   * Get category color class for UI styling
   */
  getCategoryColorClass(category: TaskCategory): string {
    const colorMap: Record<TaskCategory, string> = {
      'hydration': 'bg-blue-100 text-blue-800 border-blue-200',
      'nutrition': 'bg-green-100 text-green-800 border-green-200',
      'movement': 'bg-orange-100 text-orange-800 border-orange-200',
      'supplements': 'bg-purple-100 text-purple-800 border-purple-200',
      'sleep': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'mood': 'bg-pink-100 text-pink-800 border-pink-200',
      'stress': 'bg-red-100 text-red-800 border-red-200',
      'energy': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'health': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'other': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colorMap[category] ?? 'bg-gray-100 text-gray-800 border-gray-200';
  }

  /**
   * Get status display info
   */
  getStatusDisplayInfo(status: TaskStatus): { text: string; color: string; icon: string } {
    const statusMap: Record<TaskStatus, { text: string; color: string; icon: string }> = {
      'pending': { text: 'Pending', color: 'text-gray-600', icon: '⏳' },
      'active': { text: 'In Progress', color: 'text-blue-600', icon: '🔄' },
      'completed': { text: 'Completed', color: 'text-green-600', icon: '✅' },
      'skipped': { text: 'Skipped', color: 'text-yellow-600', icon: '⏭️' }
    };
    return statusMap[status] || statusMap['pending'];
  }

  /**
   * Get priority display info
   */
  getPriorityDisplayInfo(priority: TaskPriority): { text: string; color: string; icon: string } {
    const priorityMap: Record<TaskPriority, { text: string; color: string; icon: string }> = {
      'low': { text: 'Low', color: 'text-gray-500', icon: '⬇️' },
      'regular': { text: 'Normal', color: 'text-blue-500', icon: '➡️' },
      'high': { text: 'High', color: 'text-red-500', icon: '⬆️' }
    };
    return priorityMap[priority] || priorityMap['regular'];
  }

  /**
   * Get estimated completion time
   */
  getEstimatedCompletionTime(hour: number, minute: number, duration: number): string {
    const startTime = new Date();
    startTime.setHours(hour, minute, 0, 0);
    
    const endTime = new Date(startTime.getTime() + duration * 60000);
    
    return this.getFormattedTime(endTime.getHours(), endTime.getMinutes());
  }

  /**
   * Get time until task
   */
  getTimeUntilTask(hour: number, minute: number): string {
    const now = new Date();
    const taskTime = new Date();
    taskTime.setHours(hour, minute, 0, 0);
    
    // If task time is in the past, assume it's for tomorrow
    if (taskTime <= now) {
      taskTime.setDate(taskTime.getDate() + 1);
    }
    
    const diffMs = taskTime.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `in ${diffHours}h ${diffMinutes}m`;
    } if (diffMinutes > 0) {
      return `in ${diffMinutes}m`;
    } 
      return 'now';
    
  }

  /**
   * Sort tasks by scheduled time
   */
  sortTasksByTime(tasks: ScheduledTask[]): ScheduledTask[] {
    return [...tasks].sort((a, b) => {
      const timeA = a.targetTimeHour * 60 + a.targetTimeMinute;
      const timeB = b.targetTimeHour * 60 + b.targetTimeMinute;
      return timeA - timeB;
    });
  }

  /**
   * Filter tasks by criteria
   */
  filterTasks(tasks: ScheduledTask[], criteria: {
    status?: TaskStatus[];
    category?: TaskCategory[];
    priority?: TaskPriority[];
  }): ScheduledTask[] {
    return tasks.filter(task => {
      if (criteria.status && !criteria.status.includes(task.status)) {
        return false;
      }
      if (criteria.category && !criteria.category.includes(task.category)) {
        return false;
      }
      if (criteria.priority && !criteria.priority.includes(task.priority)) {
        return false;
      }
      return true;
    });
  }

  // Private helper methods
  private calculateOverallProgress(sections: TaskSection[]): number {
    if (sections.length === 0) {return 0;}
    
    const totalProgress = sections.reduce((sum, section) => sum + section.progress, 0);
    return Math.round(totalProgress / sections.length);
  }

  private calculateTotalTasks(sections: TaskSection[]): number {
    return sections.reduce((sum, section) => sum + (section.totalTasks ?? 0), 0);
  }

  private calculateCompletedTasks(sections: TaskSection[]): number {
    return sections.reduce((sum, section) => sum + (section.completedTasks ?? 0), 0);
  }
}

export const tasksService = new TasksService();
export { TasksService };